import { Button, Form, Select, SelectProps, Spin } from "antd";
import { notificationInstance } from "App";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatUsername } from "utils/formatUsername";

interface ChangeFileOwnerProps extends ModalComponentProps {
  projectId: number;
}

export const ChangeFileOwner: FC<ChangeFileOwnerProps> = ({ hideModal, projectId }) => {
  const [fileOwnersInProject, setFileOwnersInProject] = useState<SelectProps["options"]>([]);
  const [projectUsers, setProjectUsers] = useState<SelectProps["options"]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { t } = useTranslation(I18N_NS.ADMIN);

  const handleFinish = async (values: { oldOwnerId: number; ownerId: number }) => {
    try {
      setLoading(true);
      const formData: API_ChangeFileOwnerRequest = {
        projectId,
        oldOwnerId: values.oldOwnerId,
        ownerId: values.ownerId,
      };
      const response = await API.editData<string>(API_URL.ADMIN_PROJECTS.changeFileOwnerInProject(), formData, null, {
        responseType: "text",
      });
      notificationInstance.success({
        title: t("Success"),
        description: t("The owner of {{amount}} files was changed.", { amount: response }),
      });
      hideModal();
    } catch (error) {
      console.error("Error changing file owner:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [owners, users] = await Promise.all([
          API.getData<API_User[]>(API_URL.ADMIN_PROJECTS.getAllFileOwnersInProject(projectId)),
          API.getData<API_User[]>(API_URL.PROJECTS.getUsersByProjectId_1(projectId), { hideInactive: true }),
        ]);
        setProjectUsers(
          users.map((owner) => ({
            label: formatUsername(owner),
            value: owner.id,
          }))
        );
        setFileOwnersInProject(
          owners.map((owner) => ({
            label: formatUsername(owner),
            value: owner.id,
          }))
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Spin spinning={loading}>
      <Form layout="horizontal" requiredMark={false} onFinish={handleFinish}>
        <Form.Item name="oldOwnerId" rules={[{ required: true, message: t("This field is required") }]}>
          <Select
            loading={loading}
            placeholder={t("Old owner")}
            options={fileOwnersInProject}
            showSearch={{
              filterOption: (inputValue = "", option) =>
                (option.label as string)?.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1,
            }}
          />
        </Form.Item>
        <Form.Item name="ownerId" rules={[{ required: true, message: t("This field is required") }]}>
          <Select
            loading={loading}
            placeholder={t("New owner")}
            showSearch={{
              filterOption: (inputValue = "", option) =>
                (option.label as string)?.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1,
            }}
            options={projectUsers}
          />
        </Form.Item>
        <div className="text-right">
          <Button type="primary" htmlType="submit" loading={loading}>
            {t("Submit")}
          </Button>
        </div>
      </Form>
    </Spin>
  );
};
